/**
 * 이상한 방 - 여럿이 첫 바꾸기 안내 (해 보면서 배우는 세 걸음, 순수 DOM)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(바꾸기 1분) · 4.2절(선택 → 동사 → 즉시 실행)
 *            `이상한방-상태모델-v0.1.md` 9.5절(싱글이 튜토리얼이다) · 원칙 2(UI가 덮은 면적은 곧 볼 수 없는 방의 넓이)
 *
 * ## 왜 여럿이에만 안내가 따로 필요한가
 *
 * 9.5절은 튜토리얼을 싱글에 맡겼는데, 싱글에는 **바꾸기 단계가 없다.** 혼자 하는 판에서 사람은 언제나 찾는 쪽이고
 * 변화는 생성기가 만든다. 그래서 여럿이에 처음 들어온 사람은 "네 구획을 손봐라"라는 자막과 다섯 개짜리 띠를
 * 난생처음 같이 만난다 - 실제로 친구 판에서 나온 첫 보고가 "뭘 눌러야 하는지 모르겠다"였다.
 *
 * ## 읽는 안내가 아니라 **해 보는** 안내다
 *
 * 바꾸기는 60초짜리 페이즈다. 그 앞에 설명 판을 세우면 읽는 동안 시간이 간다. 그래서 이 카드는 **읽고 넘기는 것이
 * 아니라 지금 할 일 한 줄**이고, 사람이 실제로 그것을 하면 다음 걸음으로 넘어간다:
 *
 *  1. 아직 하나도 안 만들었다 → 어떻게 **생성**하는가
 *  2. 하나 이상 만들었고 예산이 남았다 → 이미 놓은 것도 어떻게 **고치는가**
 *  3. 예산을 다 썼다 → [다 했다]
 *
 * 걸음의 재료는 띠가 그리는 뷰(`ChangerBarView`) 그대로다. **여기서 세지 않는다** - `budget - used`를 다시 세면
 * 띠와 안내가 다른 수를 말하는 날이 오고, 그건 안내가 아니라 혼란이다.
 *
 * ## 한 번 배운 사람에게 다시 말하지 않는다
 *
 * 마쳤다는 사실은 계정에 남는다(`game/progress.coached`). 3번 걸음에 닿거나 사람이 [알겠다]를 누르면 그 순간
 * 마친 것이다 - 커밋까지 기다리지 않는 이유는, 시간이 다 되어 자동 마감된 사람도 두 걸음을 이미 배웠기 때문이다.
 *
 * ## 자리: 가운데 알림 열의 맨 아래
 *
 * 방을 덮지 않아야 하고(원칙 2) 띠를 가리면 안 된다 - 가리키는 대상이 그 띠다. 그래서 위쪽 가운데 열의 마지막 칸에
 * 선다(`overlayLayout.OVERLAY_SLOTS.coach`). 자막(`notice`)보다 아래인 것은 자막이 "왜 안 되는가"라서 늘 더 급하기 때문이다.
 */

import { t } from '../i18n'
import type { MessageKey } from '../i18n/ko'

import { createElement, setHidden, setText } from './dom'
import { overlayLayout } from './overlayLayout'

import './changerCoach.css'

/* --- 걸음 ---------------------------------------------------------------------- */

/**
 * 지금 할 일. **뷰 하나에서 갈리고 다른 입력이 없다** - 상태를 따로 들면 "화면은 2번인데 실제로는 3번"이 생긴다.
 *  - `place` 아직 하나도 안 만들었다
 *  - `edit`  만들었고 예산이 남았다
 *  - `done`  예산을 다 썼다
 */
export type CoachStep = 'place' | 'edit' | 'done'

/** 걸음을 가르는 것은 이 함수 하나다. 순수하므로 화면 없이 검사된다. */
export function coachStepOf(view: Readonly<{ used: number; left: number }>): CoachStep {
  if (view.left <= 0) return 'done'
  return view.used <= 0 ? 'place' : 'edit'
}

/**
 * 걸음 → 문구 키. **표로 적는다** - `coach.changer.${step}`로 조립하면 사전에 없는 키가 컴파일을 통과하고,
 * 화면에는 키 문자열이 그대로 뜬다(`i18n/index.ts`의 `t`가 `string & {}`도 받는 이유는 다른 데 있다).
 */
const COACH_TEXT: Readonly<Record<CoachStep, Readonly<{ title: MessageKey; hint: MessageKey }>>> = {
  place: { title: 'coach.changer.place', hint: 'coach.changer.place.hint' },
  edit: { title: 'coach.changer.edit', hint: 'coach.changer.edit.hint' },
  done: { title: 'coach.changer.done', hint: 'coach.changer.done.hint' },
}

/** 이 걸음이 안내의 끝인가. 여기 닿으면 계정에 "배웠다"가 남는다(머리말). */
export function isLastCoachStep(step: CoachStep): boolean {
  return step === 'done'
}

/* --- 타입 ---------------------------------------------------------------------- */

/** 안내가 읽는 뷰의 조각. `ChangerBarView`가 이것을 구조적으로 만족한다 - 도구의 타입을 `ui/`가 import 하지 않는다. */
export type ChangerCoachView = Readonly<{ used: number; left: number }>

export type ChangerCoachHandle = Readonly<{
  root: HTMLElement
  /** 지금 뷰로 다시 그린다. 띠와 **같은 순간에** 불린다(`app/stageChanger.ts`의 `onView`). */
  render: (view: ChangerCoachView) => void
  dispose: () => void
}>

export type ChangerCoachOptions = Readonly<{
  mount: HTMLElement
  /** 안내를 마쳤다. 부르는 쪽이 계정에 남긴다 - `ui/`는 저장소를 모른다(`app/netBoot.ts`와 같은 규약). */
  onDone: () => void
}>

/* --- 본체 ---------------------------------------------------------------------- */

export function createChangerCoach(options: ChangerCoachOptions): ChangerCoachHandle {
  const root = createElement('aside', 'cc-root')
  // `status`인 이유: 사람이 무엇을 해야 하는지를 말하는 줄이라 낭독기가 바뀔 때마다 읽어야 한다.
  root.setAttribute('role', 'status')
  root.hidden = true

  const title = createElement('p', 'cc-title', '')
  const hint = createElement('p', 'cc-hint', '')
  const close = createElement('button', 'cc-close', t('coach.changer.close'))
  close.type = 'button'

  const body = createElement('div', 'cc-body')
  body.append(title, hint)
  root.append(body, close)

  const layout = overlayLayout(options.mount)
  layout.place(root, 'coach')

  /** 걷혔는가. 한 번 걷히면 이 판에서는 다시 서지 않는다 - 껐는데 다시 뜨는 것이 가장 나쁜 안내다. */
  let retired = false
  /** 계정에 이미 알렸는가. 마지막 걸음은 뷰가 올 때마다 다시 오므로 문을 한 번만 지난다. */
  let reported = false
  let shown: CoachStep | null = null

  function markLearned(): void {
    if (reported) return
    reported = true
    options.onDone()
  }

  function retire(): void {
    retired = true
    setHidden(root, true)
    markLearned()
  }

  close.addEventListener('click', retire)

  return {
    root,

    render(view: ChangerCoachView): void {
      if (retired) return

      const step = coachStepOf(view)
      if (step !== shown) {
        shown = step
        const text = COACH_TEXT[step]
        setText(title, t(text.title))
        setText(hint, t(text.hint))
      }
      setHidden(root, false)

      // 마지막 걸음까지 갔으면 배운 것이다. 카드는 그 한 줄을 말한 채로 남고, 계정에만 표시가 간다 -
      // 여기서 걷어 버리면 [다 했다]를 누르라는 말이 그 말을 하는 순간 사라진다.
      if (isLastCoachStep(step)) markLearned()
    },

    dispose(): void {
      // 기둥에서 걷는다 - `remove()`만 하면 관측기가 떼어 낸 노드를 계속 붙든다(`overlayLayout` 규약).
      layout.unplace(root)
    },
  }
}
